import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { File } from '../lib/conflix';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { FolderPlus, Upload, FileIcon, FolderIcon, ArrowUp, RefreshCw, CreditCard, Trash2 } from 'lucide-react';
import { formatFileSize, formatDate } from '../lib/utils';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { c } from 'vite/dist/node/types.d-aGj9QkWt';

const apiUrl = process.env.REACT_APP_API_URL;

interface StorageInfo {
  usage: number;
  limit: number;
  isPro: boolean;
  canUpload: boolean;
  usagePercentage: number;
}

interface FilesProps {
  idToken: string;
}

const Files: React.FC<FilesProps> = ({ idToken }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [storageInfo, setStorageInfo] = useState<StorageInfo>({
    usage: 0,
    limit: 0,
    isPro: false,
    canUpload: true,
    usagePercentage: 0
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [showNewFolderDialog, setShowNewFolderDialog] = useState<boolean>(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file: File | null }>({ x: 0, y: 0, file: null });
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [fileToDelete, setFileToDelete] = useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Fetch files from the current path
  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${apiUrl}files`, {
        params: { path: currentPath },
        headers: {
          Authorization: idToken
        },
      });
      
      if (response.status === 200) {
        // The API now returns an object with files and storage info
        setFiles(response.data.files || []);
        
        // Set storage info if available
        if (response.data.storage) {
          setStorageInfo(response.data.storage);
          
          // Show upgrade prompt if not Pro and using more than 80% of storage
          if (!response.data.storage.isPro && 
              response.data.storage.usagePercentage > 80) {
            setShowUpgradePrompt(true);
          } else {
            setShowUpgradePrompt(false);
          }
        }
      } else {
        setError('Failed to fetch files');
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to fetch files. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [idToken, currentPath]);

  // Initial load
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Navigate to a folder
  const navigateToFolder = (folder: File) => {
    if (folder.isFolder) {
      setCurrentPath(folder.path);
      console.log('Navigating to folder:', folder.path);
    }
  };

  // Navigate up one level
  const navigateUp = () => {
    // Don't go above root
    if (currentPath === '/') {
      return;
    }

    // Remove the last path segment
    const pathParts = currentPath.split('/').filter(Boolean);
    pathParts.pop();
    const newPath = pathParts.length ? `/${pathParts.join('/')}/` : '/';
    setCurrentPath(newPath);
    console.log('Navigating up to:', newPath);
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList) => {
    if (!files.length) return;
    
    setIsUploading(true);
    setError(null);
    
    // Check if there's enough storage space
    if (!storageInfo.canUpload) {
      setError(storageInfo.isPro
        ? 'You have reached your 1TB storage limit.'
        : 'You have reached your 5GB free storage limit. Upgrade to Pro for 1TB of storage.');
      setIsUploading(false);
      setShowUpgradePrompt(!storageInfo.isPro);
      return;
    }
    
    const uploadPromises = Array.from(files).map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${apiUrl}files/upload`, formData, {
          headers: {
            Authorization: idToken,
            'X-Upload-Path': currentPath
            // Don't set Content-Type - Axios will automatically set it with the correct boundary
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          },
        });
        
        // Update storage info if it's returned
        if (response.data.storage) {
          setStorageInfo(response.data.storage);
        }
        
        return response.data;
      } catch (error: any) {
        console.error('Upload error:', error);
        
        // Check if this is a storage limit error
        if (error.response?.data?.upgradeRequired) {
          setShowUpgradePrompt(true);
          setStorageInfo(error.response.data.storage || storageInfo);
        }
        
        throw new Error(error.response?.data?.message || `Failed to upload ${file.name}`);
      }
    });
    
    try {
      await Promise.all(uploadPromises);
      // Refresh file list after upload
      fetchFiles();
    } catch (err: any) {
      setError(err.message || 'One or more files failed to upload. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset the file input to allow uploading the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  // Create a new folder
  const createFolder = async () => {
    if (!newFolderName) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${apiUrl}files/folder`, 
        { 
          name: newFolderName,
          path: currentPath
        },
        {
          headers: {
            Authorization: idToken,  
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 201) {
        // Refresh file list after creating folder
        fetchFiles();
        setNewFolderName('');
        setShowNewFolderDialog(false);
      } else {
        setError('Failed to create folder');
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle upgrade to Pro
  const handleUpgradeToPro = () => {
    // Redirect to account page where user can upgrade
    window.history.pushState({}, '', '/account');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Handle context menu
  const handleContextMenu = (e: React.MouseEvent, file: File) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, file });
  };

  // Close context menu
  const closeContextMenu = () => {
    setContextMenu({ x: 0, y: 0, file: null });
  };

  // Handle delete file/folder
  const handleDeleteFile = async () => {
    if (!fileToDelete) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.delete(`${apiUrl}files/${fileToDelete.fileId}`, {
        headers: {
          Authorization: idToken,
        }
      });
      
      if (response.status === 200) {
        // Refresh file list after deletion
        fetchFiles();
        setShowDeleteDialog(false);
        setFileToDelete(null);
      } else {
        setError('Failed to delete file');
      }
    } catch (err: any) {
      console.error('Error deleting file:', err);
      setError(err.response?.data?.message || 'Failed to delete file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if file can be deleted (protect "matters" folder at root only)
  const canDeleteFile = (file: File) => {
    if (file.isFolder && file.name === 'matters' && currentPath === '/') {
      return false;
    }
    return true;
  };

  // Handle delete button click
  const handleDeleteClick = (file: File) => {
    if (!canDeleteFile(file)) {
      setError('The root "matters" folder cannot be deleted.');
      return;
    }
    setFileToDelete(file);
    setShowDeleteDialog(true);
    closeContextMenu();
  };

  return (
    <div className="container mx-auto p-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Files</CardTitle>
              <CardDescription>
                Manage your organization's files
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowNewFolderDialog(true)}
                disabled={!storageInfo.canUpload}
              >
                <FolderPlus className="mr-2 h-4 w-4" /> New Folder
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={!storageInfo.canUpload}
              >
                <Upload className="mr-2 h-4 w-4" /> Upload
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                multiple
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
              />
              <Button
                variant="ghost"
                onClick={fetchFiles}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Storage usage bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Storage: {formatFileSize(storageInfo.usage)} / {formatFileSize(storageInfo.limit)}</span>
              {storageInfo.isPro ? (
                <span className="text-green-600 font-medium">Pro Plan</span>
              ) : (
                <span className="text-gray-600">Free Plan</span>
              )}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className={`h-2.5 rounded-full ${storageInfo.usagePercentage > 90 
                  ? 'bg-red-600' 
                  : storageInfo.usagePercentage > 70 
                    ? 'bg-yellow-400' 
                    : 'bg-blue-600'}`}
                style={{ width: `${Math.min(storageInfo.usagePercentage, 100)}%` }}
              ></div>
            </div>
          </div>
          
          {/* Upgrade prompt for free users */}
          {showUpgradePrompt && !storageInfo.isPro && (
            <div className="mt-4 bg-blue-50 p-3 rounded-md border border-blue-100 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-blue-800">Need more storage?</h4>
                <p className="text-xs text-blue-600">Upgrade to Pro for 1TB of storage (200x more than free)</p>
              </div>
              <Button 
                size="sm"
                onClick={handleUpgradeToPro}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CreditCard className="mr-2 h-4 w-4" /> Upgrade to Pro
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent 
          className="p-0"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Path navigation */}
          <div className="flex items-center p-4 border-b">
            <Button
              variant="ghost"
              size="sm"
              onClick={navigateUp}
              disabled={currentPath === '/'}
            >
              <ArrowUp className="h-4 w-4 mr-1" /> Up
            </Button>
            <div className="ml-4 text-sm text-gray-500">
              Current path: <span className="font-mono">{currentPath}</span>
            </div>
          </div>

          {/* Error alert */}
          {error && (
            <div className="p-4">
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}

          {/* Upload progress */}
          {isUploading && (
            <div className="p-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-center mt-2">Uploading: {uploadProgress}%</p>
            </div>
          )}

          {/* Files and folders list */}
          <div className="min-h-[300px]">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FolderIcon className="h-16 w-16 mb-4" />
                <p className="text-lg">This folder is empty</p>
                <p className="text-sm">Upload files or create a new folder</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Last Modified By</TableHead>
                    <TableHead>Last Modified At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file) => (
                    <TableRow 
                      key={file.fileId}
                      onContextMenu={(e) => handleContextMenu(e, file)}
                    >
                      <TableCell>
                        <div className="flex items-center">
                          {file.isFolder ? (
                            <button
                              className="flex items-center hover:text-blue-600"
                              onClick={() => navigateToFolder(file)}
                            >
                              <FolderIcon className="h-5 w-5 mr-2 text-yellow-400" />
                              {file.name}
                            </button>
                          ) : (
                            <div className="flex items-center">
                              <FileIcon className="h-5 w-5 mr-2 text-gray-400" />
                              <a 
                                href={file.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:text-blue-600"
                              >
                                {file.name}
                              </a>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{file.isFolder ? '--' : formatFileSize(file.size)}</TableCell>
                      <TableCell>{file.createdBy}</TableCell>
                      <TableCell>{formatDate(file.createdAt)}</TableCell>
                      <TableCell>{file.lastModifiedBy}</TableCell>
                      <TableCell>{formatDate(file.lastModifiedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
        <CardFooter className="text-sm text-gray-500 border-t p-4 flex justify-between">
          {!loading && (
            <>
              <p>
                {files.length} {files.length === 1 ? 'item' : 'items'}
              </p>
              <p>
                {storageInfo.isPro ? 'Pro' : 'Free'} Storage: {formatFileSize(storageInfo.usage)} / {formatFileSize(storageInfo.limit)}
                {!storageInfo.isPro && (
                  <Button 
                    variant="link" 
                    className="text-blue-600 p-0 h-auto ml-2 inline-flex items-center"
                    onClick={handleUpgradeToPro}
                  >
                    <CreditCard className="h-3 w-3 mr-1" /> Upgrade to Pro
                  </Button>
                )}
              </p>
            </>
          )}
        </CardFooter>
      </Card>

      {/* Context Menu */}
      {contextMenu.file && (
        <div
          className="fixed z-50 min-w-48 bg-white border border-gray-200 rounded-md shadow-lg"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div className="py-1">
            <button
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              onClick={() => handleDeleteClick(contextMenu.file!)}
              disabled={!canDeleteFile(contextMenu.file)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete {contextMenu.file.isFolder ? 'Folder' : 'File'}
            </button>
          </div>
        </div>
      )}

      {/* Background overlay to close context menu */}
      {contextMenu.file && (
        <div 
          className="fixed inset-0 z-40"
          onClick={closeContextMenu}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {fileToDelete?.isFolder ? 'Folder' : 'File'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{fileToDelete?.name}"? 
              {fileToDelete?.isFolder && ' This will also delete all files inside this folder.'}
              {' '}This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteFile}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name for the new folder
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolderDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createFolder}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Files;
